import React, { useEffect,useState } from 'react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';

import { type InsuranceInfo, type PatientAddress } from '../../types/types';

import AddressCard from './AddressCard';
import PayerSearch from './PayerSearch';

interface InsuranceCardProps {
  insurance: InsuranceInfo | null | undefined;
  onChange: (insurance: InsuranceInfo | null) => void;
  onBlur?: () => void;
  title?: string;
  showDelete?: boolean;
  onDelete?: () => void;
  errors?: Record<string, string>;
  highlightedFields?: string[];
  disabled?: boolean;
}

const InsuranceCard: React.FC<InsuranceCardProps> = ({
  insurance,
  onChange,
  onBlur,
  title = 'Insurance Information',
  showDelete = false,
  onDelete,
  errors = {},
  highlightedFields = [],
  disabled = false,
}) => {
  const [showInsuredDetails, setShowInsuredDetails] = useState(false);

  useEffect(() => {
    // Show insured details if relationship is not 'self' or if there's existing insured data
    if (insurance?.relationship_to_insured && insurance.relationship_to_insured !== 'self') {
      setShowInsuredDetails(true);
    }
  }, [insurance?.relationship_to_insured]);

  const handleFieldChange = (field: keyof InsuranceInfo, value: string | null | boolean | PatientAddress) => {
    const updatedInsurance: InsuranceInfo = {
      ...(insurance || {}),
      [field]: value === '' ? null : value,
    };
    onChange(updatedInsurance);
  };

  const handlePayerSelect = (payer: { id: string; name: string; eligibility: string } | null) => {
    if (payer) {
      const updatedInsurance: InsuranceInfo = {
        ...(insurance || {}),
        payer_id: payer.id,
        payer_name: payer.name,
      };
      onChange(updatedInsurance);
    } else {
      const updatedInsurance: InsuranceInfo = {
        ...(insurance || {}),
        payer_id: null,
        payer_name: null,
      };
      onChange(updatedInsurance);
    }
  };

  const handleRelationshipChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    handleFieldChange('relationship_to_insured', value);
    
    // Show/hide insured details based on relationship
    if (value !== 'self' && value !== '') {
      setShowInsuredDetails(true);
    } else {
      setShowInsuredDetails(false);
      // Clear insured fields if relationship is self
      if (value === 'self') {
        const updatedInsurance: InsuranceInfo = {
          ...(insurance || {}),
          relationship_to_insured: 'self',
          insured_name: null,
          insured_dob: null,
          insured_sex: null,
          insured_address: null,
        };
        onChange(updatedInsurance);
      }
    }
  };

  const handleInsuredAddressChange = (address: PatientAddress | null) => {
    handleFieldChange('insured_address', address);
  };

  const selectedPayer = insurance?.payer_id && insurance?.payer_name
    ? { id: insurance.payer_id, name: insurance.payer_name, eligibility: '' }
    : null;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
          {title}
        </Typography>
        {showDelete && onDelete && (
          <IconButton size="small" onClick={onDelete} color="error" disabled={disabled}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Grid container spacing={2}>
        {/* Payer Search */}
        <Grid item xs={12}>
          <PayerSearch
            onPayerSelect={handlePayerSelect}
            disabled={disabled}
            value={selectedPayer}
          />
        </Grid>

        {/* Payer ID (Read-only) */}
        <Grid item xs={12} sm={6}>
          <TextField
            label="Payer ID"
            value={insurance?.payer_id || ''}
            variant="outlined"
            size="small"
            fullWidth
            InputProps={{ readOnly: true }}
            disabled={disabled}
            helperText="Automatically filled from payer selection"
            sx={{
              ...(highlightedFields.includes('payer_id') && {
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: 2,
                  },
                },
              }),
            }}
          />
        </Grid>

        {/* Plan Name */}
        <Grid item xs={12} sm={6}>
          <TextField
            label="Plan Name (Optional)"
            value={insurance?.plan_name || ''}
            onChange={(e) => handleFieldChange('plan_name', e.target.value)}
            onBlur={onBlur}
            variant="outlined"
            size="small"
            fullWidth
            disabled={disabled}
            error={!!errors.plan_name}
            helperText={errors.plan_name}
          />
        </Grid>

        {/* Policy Number */}
        <Grid item xs={12} sm={6}>
          <TextField
            label="Policy/ID Number"
            value={insurance?.policy_number || ''}
            onChange={(e) => handleFieldChange('policy_number', e.target.value)}
            onBlur={onBlur}
            variant="outlined"
            size="small"
            fullWidth
            disabled={disabled}
            error={!!errors.policy_number}
            helperText={errors.policy_number}
            sx={{
              ...(highlightedFields.includes('policy_number') && {
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: 2,
                  },
                },
              }),
            }}
          />
        </Grid>

        {/* Group Number */}
        <Grid item xs={12} sm={6}>
          <TextField
            label="Group Number (Optional)"
            value={insurance?.group_number || ''}
            onChange={(e) => handleFieldChange('group_number', e.target.value)}
            onBlur={onBlur}
            variant="outlined"
            size="small"
            fullWidth
            disabled={disabled}
            error={!!errors.group_number}
            helperText={errors.group_number}
          />
        </Grid>

        {/* Relationship to Insured */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small" disabled={disabled}>
            <InputLabel>Relationship to Insured</InputLabel>
            <Select
              value={insurance?.relationship_to_insured || ''}
              onChange={handleRelationshipChange}
              onBlur={onBlur}
              label="Relationship to Insured"
              sx={{
                ...(highlightedFields.includes('relationship_to_insured') && {
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: 'primary.main',
                      borderWidth: 2,
                    },
                  },
                }),
              }}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              <MenuItem value="self">Self</MenuItem>
              <MenuItem value="spouse">Spouse</MenuItem>
              <MenuItem value="child">Child</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Authorization on File */}
        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={
              <Checkbox
                checked={insurance?.authorization_on_file || false}
                onChange={(e) => handleFieldChange('authorization_on_file', e.target.checked)}
                disabled={disabled}
              />
            }
            label="Authorization Signature on File"
          />
        </Grid>

        {/* Insured Person Details (if different from patient) */}
        {showInsuredDetails && (
          <>
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Insured Person Information
                </Typography>
              </Divider>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Insured Name"
                value={insurance?.insured_name || ''}
                onChange={(e) => handleFieldChange('insured_name', e.target.value)}
                onBlur={onBlur}
                variant="outlined"
                size="small"
                fullWidth
                disabled={disabled}
                error={!!errors.insured_name}
                helperText={errors.insured_name}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField
                label="Insured Date of Birth"
                type="date"
                value={insurance?.insured_dob || ''}
                onChange={(e) => handleFieldChange('insured_dob', e.target.value)}
                onBlur={onBlur}
                variant="outlined"
                size="small"
                fullWidth
                disabled={disabled}
                InputLabelProps={{ shrink: true }}
                error={!!errors.insured_dob}
                helperText={errors.insured_dob}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small" disabled={disabled}>
                <InputLabel>Insured Sex</InputLabel>
                <Select
                  value={insurance?.insured_sex || ''}
                  onChange={(e) => handleFieldChange('insured_sex', e.target.value)}
                  onBlur={onBlur}
                  label="Insured Sex"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Insured Address */}
            <Grid item xs={12}>
              <Box sx={{ mt: 1 }}>
                <AddressCard
                  address={insurance?.insured_address}
                  onChange={handleInsuredAddressChange}
                  onBlur={onBlur}
                  title="Insured Address"
                  disabled={disabled}
                  errors={errors}
                  highlightedFields={highlightedFields}
                />
              </Box>
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
};

export default InsuranceCard;